import { AidFrontendFeatureGate } from '../providers/FeatureGates';
import { Action, EmailConfirmationError, ThunkAction } from '../types';
import submitFormHack, { submitFormHackReplacement } from '../utilities/submit-form';
import { checkFeatureGate } from '../utilities/feature-flags/feature-gate';

import { State as ChangeEmailState } from '../reducers/change-email-reducer';

export type ChangeEmailAction =
  | { type: 'CHANGE_EMAIL_RESET_STATE' }
  | { type: 'CHANGE_EMAIL_REQUEST' }
  | { type: 'CHANGE_EMAIL_REQUEST_FAILED'; error: EmailConfirmationError }
  | { type: 'CHANGE_EMAIL_REQUEST_SUCCEEDED'; redirectTo: string }
  | { type: 'CHANGE_EMAIL_STATE_UPDATE'; backendState: Partial<ChangeEmailState> };

export const resetChangeEmailStateAction = (): Action => ({
  type: 'CHANGE_EMAIL_RESET_STATE',
});

export const changeEmailRequestAction = (): Action => ({
  type: 'CHANGE_EMAIL_REQUEST',
});

export const changeEmailRequestFailedAction = (error: EmailConfirmationError): Action => ({
  type: 'CHANGE_EMAIL_REQUEST_FAILED',
  error,
});
export const changeEmailRequestSucceededAction = (redirectTo: string): Action => ({
  type: 'CHANGE_EMAIL_REQUEST_SUCCEEDED',
  redirectTo,
});

export const changeEmailStateUpdateAction = (backendState: Partial<ChangeEmailState>): Action => ({
  type: 'CHANGE_EMAIL_STATE_UPDATE',
  backendState,
});

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const CHANGE_EMAIL_REST_ENDPOINT = '/rest/verify-email/change-email';

export const changeEmailConfirmation =
  (email: string, password: string, token: string, userId: string, source: string): ThunkAction =>
  async (dispatch, getState) => {
    // Fork to use either the old submitFormHack or new submitFormHackReplacement function, well as the old or new endpoint
    // old endpoint: /verify-email/change-email (submitFormHack - form submission with input div manipulation)
    // new endpoint: /rest/verify-email/change-email (submitFormHackReplacement - REST POST request with JSON payload)
    if (!checkFeatureGate(AidFrontendFeatureGate.ENABLE_JSON_ENDPOINT_FOR_VERIFY_EMAIL)) {
      submitFormHack({
        method: 'POST',
        payload: { email, password },
        skipSanitizationKeys: ['password'],
      });

      return;
    }

    try {
      const csrfToken = getState().csrfToken;

      const res = await submitFormHackReplacement({
        method: 'POST',
        // These three parameters are included in the link from the verification email, therefore including to match existing functionality.
        action: `${CHANGE_EMAIL_REST_ENDPOINT}?token=${token}&userId=${userId}&source=${source}`,
        payload: { email, password },
        csrfToken,
      });

      // The endpoint returns structured validation errors on non-2xx responses.
      const response: unknown = await res.json();
      if (!isRecord(response)) {
        dispatch(changeEmailRequestFailedAction('unknown_error'));
        return;
      }

      const changeEmail = response.changeEmail;
      if (changeEmail !== undefined && !isRecord(changeEmail)) {
        dispatch(changeEmailRequestFailedAction('unknown_error'));
        return;
      }

      // Only copy form data. Error and redirect state must pass their own checks below.
      if (isRecord(changeEmail)) {
        const backendState: Partial<ChangeEmailState> = {};
        if (typeof changeEmail.email === 'string') {
          backendState.email = changeEmail.email;
        }
        if (typeof changeEmail.newEmail === 'string') {
          backendState.newEmail = changeEmail.newEmail;
        }
        if (Object.keys(backendState).length > 0) {
          dispatch(changeEmailStateUpdateAction(backendState));
        }
      }

      const error = changeEmail?.error;
      if (error !== undefined && error !== null) {
        if (typeof error !== 'string') {
          dispatch(changeEmailRequestFailedAction('unknown_error'));
          return;
        }
        switch (error) {
          case 'invalid_token':
          case 'invalid_password':
          case 'missing_password':
          case 'unknown_error':
            dispatch(changeEmailRequestFailedAction(error));
            return;
          default:
            dispatch(changeEmailRequestFailedAction('unknown_error'));
            return;
        }
      }

      if (!res.ok || response.error !== undefined) {
        dispatch(changeEmailRequestFailedAction('unknown_error'));
        return;
      }

      // Success responses use a top-level redirect; retain support for the nested form.
      const redirectTo = changeEmail?.redirectTo ?? response.redirectTo;
      if (typeof redirectTo !== 'string' || !redirectTo.trim()) {
        dispatch(changeEmailRequestFailedAction('unknown_error'));
        return;
      }

      dispatch(changeEmailRequestSucceededAction(redirectTo));
      return;
    } catch (error) {
      dispatch(changeEmailRequestFailedAction('unknown_error'));
    }
  };
